<template>
  <vs-button
    v-if="hubsArray.length === 1 && !showManageClientsButton"
    :full-width="fullWidth ?? false"
    data-test="request-new-job"
    :label="label"
    icon="add"
    @click="openNewJobSideSheet(hubsArray[0].id)"
  />

  <v-menu
    v-else
    v-model="showMenu"
    :close-on-content-click="false"
    :nudge-bottom="10"
    :nudge-width="240"
    class="rounded-xl"
    offset-y
    rounded="xl"
    origin="top right"
  >
    <template #activator="{ on }">
      <vs-button
        :full-width="fullWidth ?? false"
        data-test="open-job-hub-selector"
        :label="label"
        icon="add"
        v-on="on"
      />
    </template>

    <v-card v-if="showMenu">
      <div :class="headerClass">
        <v-subheader v-if="limitedHubs.length" class="list-subheader"> Who's it for? </v-subheader>
        <vs-text-input
          v-if="limitedHubs.length && limitedHubs.length >= 10"
          ref="searchBar"
          v-model="searchTerm"
          label="Look for ..."
          class="search-bar"
        >
          <template #append>
            <v-icon>search</v-icon>
          </template>
        </vs-text-input>
      </div>

      <div v-if="hubsArray?.length">
        <v-list ref="hubsList" class="scrollable">
          <v-list-item v-for="hub in hubsArray" :key="hub.id" @click="openNewJobSideSheet(hub.id)">
            <vs-text>{{ hub.name }}</vs-text>
          </v-list-item>
        </v-list>
      </div>
      <div v-else-if="limitedHubs.length">
        <v-list>
          <v-list-item>
            <vs-text>No results found for "{{ searchTerm }}"</vs-text>
          </v-list-item>
        </v-list>
      </div>

      <vs-button
        v-if="showManageClientsButton"
        full-width
        type="secondary"
        :icon="clientButtonIcon"
        :label="clientButtonText"
        rectangular
        remove-invisible-border
        class="clients-button"
        @click.stop="openCreateNewClient"
      />
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { useStore } from 'vuex-composition-helpers'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { sortBy } from 'lodash'
import { useHubs } from '@/common/useHubs'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { useScroll } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    parentRouteShortName: string
    extraNavParams?: object | null
    fullWidth?: boolean
    label?: string
    defaults?: {
      locationId: string | number | undefined
    }
    limitHubsTo?: string[]
    showManageClients?: boolean
  }>(),
  {
    extraNavParams: null,
    label: 'New job',
    defaults: undefined,
    limitHubsTo: () => [],
    showManageClients: true,
  },
)
const store = useStore()
const router = useRouter()

const searchBar = ref()
const showMenu = ref(false)
const searchTerm = ref('')

watch(showMenu, (val) => {
  if (!val) {
    searchTerm.value = ''
  } else {
    searchBar.value?.focus()
  }
})

const hubsList = ref()
const { y } = useScroll(hubsList)

const headerClass = computed(() => ({
  'header-shadow': y.value !== 0,
  'header-margin': true,
}))

const { accessibleHubs } = useHubs()

const limitedHubs = computed(() => {
  if (props.limitHubsTo.length > 0) {
    return accessibleHubs.value?.filter((h) => props.limitHubsTo.includes(h.id)) ?? []
  } else {
    return accessibleHubs.value ?? []
  }
})

const hubsArray = computed(() =>
  sortBy(limitedHubs.value, (h) => h?.name.toLowerCase()).filter((h) =>
    h?.name.toLowerCase().includes(searchTerm.value.toLowerCase()),
  ),
)

const defaultJobValues = computed(() => {
  if (props.defaults?.locationId) {
    return {
      locationId: `${props.defaults.locationId}`,
    }
  }
  return undefined
})

const openNewJobSideSheet = (hubId: string) => {
  showMenu.value = false
  router.push({
    name: `${props.parentRouteShortName}-ticket-info-panel`,
    params: {
      id: 'new',
      hubId,
      ...props.extraNavParams,
    },
    query: defaultJobValues.value,
  })
}

const openCreateNewClient = () => {
  showMenu.value = false
  router.push({
    name: `${props.parentRouteShortName}-search-clients`,
  })
}

const showManageClientsButton = computed<boolean>(
  () => store.getters['permissions/isSpAdmin'] && props.showManageClients,
)

const clientButtonText = computed<string>(() =>
  hubsArray.value.length > 0 ? 'Manage clients' : 'Add clients',
)
const clientButtonIcon = computed<string>(() => (hubsArray.value.length > 0 ? 'settings' : 'add'))
</script>

<style scoped>
.header-margin {
  margin-bottom: var(--space-small);
}
.header-shadow {
  box-shadow: var(--shadow-low);
}
.search-bar {
  margin-block-start: var(--space-base);
  padding-block-end: var(--space-base);
  margin-inline: var(--space-base);
}
.list-subheader {
  font-size: 16px;
  font-weight: bold;
  color: black !important;
  font-family: Montserrat, serif;
  padding-top: var(--space-small);
  margin-bottom: calc(var(--space-small) * -1);
}

.scrollable {
  max-height: 60vh;
  overflow-y: auto;
  padding-top: 0;
}

.clients-button {
  height: 50px;
}
.clients-button >>> .icon {
  font-size: 20px;
  padding-bottom: 2px;
}
</style>
